import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PatrolMap } from "@/components/PatrolMap";
import { completeTourRun } from "@/lib/checkpoint-scans";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, MapPin, AlertTriangle, XCircle, FileText, Sparkles, Loader2, Film } from "lucide-react";
import { PatrolPlaybackViewer } from "@/components/patrol-playback/PatrolPlaybackViewer";

export const Route = createFileRoute("/_authenticated/guard-tours/runs/$runId")({
  component: RunDetail,
});

type Summary = {
  id: string;
  tour_id: string;
  tour_name: string;
  officer_id: string;
  started_at: string;
  ended_at: string | null;
  status: string;
  completion_state: string | null;
  missed_count: number;
  total_checkpoints: number;
  scanned_checkpoints: number;
  percent_complete: number;
  duration_seconds: number;
  ai_summary?: string | null;
  ai_summary_generated_at?: string | null;
  anomaly_flags?: Array<{ code: string; severity: string; message: string }> | null;
};

type Checkpoint = {
  id: string;
  sequence: number;
  name: string;
  latitude: number | null;
  longitude: number | null;
};

type Scan = {
  id: number;
  checkpoint_id: string;
  scanned_at: string;
  method: string;
  latitude: number | null;
  longitude: number | null;
  distance_from_checkpoint_m: number | null;
};

function fmtDuration(secs: number) {
  if (!secs || secs < 0) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

function stateBadge(state: string | null, status: string) {
  const map: Record<string, { label: string; cls: string; icon: any }> = {
    completed: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", icon: CheckCircle2 },
    completed_with_missed: { label: "Completed · missed", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30", icon: AlertTriangle },
    abandoned: { label: "Abandoned", cls: "bg-red-500/15 text-red-500 border-red-500/30", icon: XCircle },
    incomplete: { label: "Incomplete", cls: "bg-muted text-muted-foreground", icon: AlertTriangle },
  };
  const key = state ?? (status === "in_progress" ? "in_progress" : "incomplete");
  if (key === "in_progress") {
    return <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">In progress</Badge>;
  }
  const e = map[key] ?? map.incomplete;
  const Icon = e.icon;
  return (
    <Badge variant="outline" className={e.cls}>
      <Icon className="mr-1 h-3 w-3" /> {e.label}
    </Badge>
  );
}

function RunDetail() {
  const { runId } = useParams({ from: "/_authenticated/guard-tours/runs/$runId" });
  const [summary, setSummary] = useState<Summary | null>(null);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Array<{ code: string; severity: string; message: string }>>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: sum } = await supabase
      .from("tour_run_summaries").select("*").eq("id", runId).single();
    setSummary(sum as Summary);
    if (sum) {
      // pull AI summary + anomalies from tour_runs (not in the summary view)
      const { data: runRow } = await supabase
        .from("tour_runs").select("ai_summary,anomaly_flags").eq("id", runId).single();
      setAiSummary((runRow as any)?.ai_summary ?? null);
      setAnomalies(((runRow as any)?.anomaly_flags ?? []) as any);
      const { data: cps } = await supabase
        .from("tour_checkpoints").select("id,sequence,name,latitude,longitude")
        .eq("tour_id", (sum as any).tour_id).order("sequence");
      setCheckpoints((cps ?? []) as Checkpoint[]);
      const { data: sc } = await supabase
        .from("checkpoint_scans")
        .select("id,checkpoint_id,scanned_at,method,latitude,longitude,distance_from_checkpoint_m")
        .eq("tour_run_id", runId).order("scanned_at");
      setScans((sc ?? []) as Scan[]);
    }
    setLoading(false);
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const endRun = async (abandoned: boolean) => {
    setEnding(true);
    try {
      await completeTourRun(runId, abandoned);
      toast.success(abandoned ? "Patrol marked as abandoned" : "Patrol completed");
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to end patrol");
    } finally {
      setEnding(false);
    }
  };

  const generateSummary = async () => {
    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("summarize-tour-run", {
        body: { runId },
      });
      if (error) throw error;
      setAiSummary((data as any)?.summary ?? null);
      setAnomalies(((data as any)?.anomalies ?? []) as any);
      toast.success("AI summary generated");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to generate summary");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !summary) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }

  const scannedCpIds = new Set(scans.map((s) => s.checkpoint_id));
  const missed = checkpoints.filter((c) => !scannedCpIds.has(c.id));
  const isActive = summary.status === "in_progress";

  // out-of-order detection (visual only)
  let priorMax = -1;
  const orderedScans = scans.map((s) => {
    const cp = checkpoints.find((c) => c.id === s.checkpoint_id);
    const seq = cp?.sequence ?? 0;
    const out = priorMax > seq;
    if (seq > priorMax) priorMax = seq;
    return { ...s, sequence: seq, name: cp?.name ?? "Unknown", outOfOrder: out };
  });

  // map data: use checkpoints as geofences (50m circles) + scan path as pings
  const mapGeofences = checkpoints
    .filter((c) => c.latitude != null && c.longitude != null)
    .map((c) => ({
      id: c.id, name: `#${c.sequence} ${c.name}`, kind: "inclusion" as const,
      center_lat: Number(c.latitude), center_lng: Number(c.longitude), radius_m: 30,
    }));
  const mapPings = scans
    .filter((s) => s.latitude != null && s.longitude != null)
    .map((s) => ({ latitude: Number(s.latitude), longitude: Number(s.longitude), recorded_at: s.scanned_at }));

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-8">
      <Link to="/guard-tours/$tourId" params={{ tourId: summary.tour_id }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {summary.tour_name}
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Patrol run</h1>
            {stateBadge(summary.completion_state, summary.status)}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Started {new Date(summary.started_at).toLocaleString()}
            {summary.ended_at && ` · Ended ${new Date(summary.ended_at).toLocaleString()}`}
          </p>
        </div>
        {isActive && (
          <div className="flex gap-2">
            <Button variant="outline" disabled={ending} onClick={() => endRun(true)}>
              <XCircle className="h-4 w-4" /> Abandon
            </Button>
            <Button disabled={ending} onClick={() => endRun(false)}>
              <CheckCircle2 className="h-4 w-4" /> End patrol
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Progress" value={`${summary.percent_complete}%`}
          sub={`${summary.scanned_checkpoints} / ${summary.total_checkpoints} scans`} />
        <StatCard label="Duration" value={fmtDuration(summary.duration_seconds)}
          sub={isActive ? "running" : "total"} icon={<Clock className="h-4 w-4" />} />
        <StatCard label="Missed" value={String(missed.length)} sub={missed.length === 1 ? "checkpoint" : "checkpoints"}
          tone={missed.length > 0 ? "warn" : "ok"} />
        <StatCard label="Scans logged" value={String(scans.length)}
          sub={`${orderedScans.filter((s) => s.outOfOrder).length} out of order`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="border-b p-3 text-sm font-medium">Map</div>
          <PatrolMap
            pings={mapPings}
            geofences={mapGeofences as any}
            fitTo="both"
            height={360}
            className="border-0"
          />
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <FileText className="h-4 w-4" /> Timeline
          </div>
          {orderedScans.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scans recorded.</p>
          ) : (
            <ol className="space-y-3">
              {orderedScans.map((s) => (
                <li key={s.id} className="flex items-start gap-3">
                  <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${s.outOfOrder ? "bg-amber-500" : "bg-emerald-500"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span>#{s.sequence} {s.name}</span>
                      {s.outOfOrder && <Badge variant="outline" className="border-amber-500/40 text-amber-500">out of order</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(s.scanned_at).toLocaleString()} · {s.method}
                      {s.distance_from_checkpoint_m != null && ` · ${s.distance_from_checkpoint_m} m`}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      <Card className="mt-6 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-primary" /> AI patrol summary
          </div>
          <Button size="sm" variant={aiSummary ? "outline" : "default"} disabled={aiLoading} onClick={generateSummary}>
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {aiSummary ? "Regenerate" : "Generate AI summary"}
          </Button>
        </div>
        {aiSummary ? (
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{aiSummary}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No summary yet. Generate one to get an operational recap.</p>
        )}
        {anomalies.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Detected anomalies</div>
            <ul className="space-y-1.5">
              {anomalies.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Badge variant="outline" className={
                    a.severity === "critical" ? "border-red-500/40 text-red-500"
                    : a.severity === "warn" ? "border-amber-500/40 text-amber-500"
                    : "border-muted-foreground/30 text-muted-foreground"
                  }>{a.severity}</Badge>
                  <span><span className="font-mono text-xs text-muted-foreground">{a.code}</span> — {a.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card className="mt-6 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 text-amber-500" /> Missed checkpoints ({missed.length})
        </div>
        {missed.length === 0 ? (
          <p className="text-sm text-muted-foreground">All checkpoints were scanned. Nice work.</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {missed.map((c) => (
              <li key={c.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">#{c.sequence}</span>
                <span className="truncate text-muted-foreground">{c.name}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-6 overflow-hidden">
        <div className="flex items-center gap-2 border-b p-3 text-sm font-medium">
          <Film className="h-4 w-4 text-primary" /> Patrol playback
        </div>
        <div className="p-4">
          <PatrolPlaybackViewer runId={runId} />
        </div>
      </Card>
    </div>
  );
}

function StatCard({
  label, value, sub, tone, icon,
}: { label: string; value: string; sub?: string; tone?: "ok" | "warn"; icon?: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {icon}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone === "warn" ? "text-amber-500" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}
